import { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import Calendar, { calendarHooks } from '@screentone/addon-calendar';
import {
  Button,
  Divider,
  Dropdown,
  Group,
  IconCalendar,
  Typography,
  useDropdownState,
  Wrapper
} from '@screentone/core';

import { ResponsiveLoader } from 'components/responsive-loader/ResponsiveLoader';
import { RevisionList } from './components/revision-list/RevisionList';
import styles from './RevisionSelector.module.scss';
import type { DateGroup } from './RevisionSelector.utils';
import { filterContentByDate } from './RevisionSelector.utils';

export type { DateGroup };

interface RevisionSelectorProps {
  onReset: () => void;
  onSelectDate: (date: number) => void;
  onSelectRevision: (publishUtc?: number) => void;
  dateGroups: DateGroup[];
  selectedDate?: number | null;
  disabled?: boolean;
  areRevisionsLoading?: boolean;
}

function preventDefault(e: Event) {
  e.preventDefault();
}

export const RevisionSelector = ({
  onReset,
  onSelectDate,
  onSelectRevision,
  dateGroups,
  selectedDate,
  disabled,
  areRevisionsLoading
}: RevisionSelectorProps) => {
  const { selectedDate: selectedDateFromCalendarHook, onSelect } = calendarHooks.useDate();
  const { open: isDropdownOpen, setOpen: setIsDropdownOpen, componentRef: dropdownRef } = useDropdownState();

  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number }>({
    top: 0,
    left: 0
  });

  const triggerRef = useRef<HTMLDivElement | null>(null);
  const dropdownContentRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (triggerRef.current && isDropdownOpen) {
      // Use requestAnimationFrame to ensure DOM is updated
      const updatePosition = () => {
        const triggerEl = triggerRef.current;
        const dropdownEl = dropdownContentRef.current;

        if (triggerEl && dropdownEl) {
          const rect = triggerEl.getBoundingClientRect();
          const dropdownWidth = dropdownEl.offsetWidth;
          const viewportWidth = window.innerWidth;

          const top = rect.bottom + 8;
          const { left } = rect;

          // Ensure dropdown doesn't go off-screen to the right
          const adjustedLeft = left + dropdownWidth > viewportWidth ? viewportWidth - dropdownWidth - 250 : left;

          setDropdownPosition({ top, left: adjustedLeft });
        }
      };

      requestAnimationFrame(updatePosition);
    }

    const element = document.getElementById('widepage-wrapper-right');

    if (element) {
      if (isDropdownOpen) {
        // Add listeners to prevent scroll
        element.addEventListener('wheel', preventDefault, { passive: false }); // For mouse wheel
        element.addEventListener('touchmove', preventDefault, { passive: false }); // For touch devices
      } else {
        // Clean up listeners
        element.removeEventListener('wheel', preventDefault);
        element.removeEventListener('touchmove', preventDefault);
      }
    }
  }, [isDropdownOpen]);

  const handleDropdownOpenToggle = () => setIsDropdownOpen(!isDropdownOpen);
  const handleDateChange = (date: Date) => {
    onSelect(date);
    onSelectDate(date.getTime());
  };

  const handleGoToToday = () => {
    onSelect(new Date());
    onReset();
  };

  const handleSelectRevision = (publishUtc?: number) => {
    onSelectRevision(publishUtc);
    setIsDropdownOpen(false);
  };

  const hasGroupsWithDate = dateGroups.some((group) => group.dates && group.dates.length > 0);

  return (
    <Dropdown
      open={isDropdownOpen}
      componentRef={dropdownRef}
      onToggle={handleDropdownOpenToggle}
      position="right"
      disabled={disabled}
      data-testid="page-history-date-button"
    >
      <Dropdown.Trigger>
        <div ref={triggerRef}>
          <Button tertiary>
            <Group gap="xs">
              <IconCalendar /> Go To Date
            </Group>
          </Button>
        </div>
      </Dropdown.Trigger>
      <Dropdown.Content position="right" padding={{ all: 'none' }}>
        {isDropdownOpen &&
          ReactDOM.createPortal(
            <div
              ref={dropdownContentRef}
              className={`st_box ${styles.dateFilterWrapper}`}
              style={{
                position: 'fixed',
                top: dropdownPosition.top,
                left: dropdownPosition.left,
                zIndex: 100
              }}
              onPointerDown={(e) => e.stopPropagation()}
            >
              <Wrapper>
                <Calendar
                  onSelect={handleDateChange}
                  numberOfMonths={1}
                  showCalendarOnly
                  dateFormat="MM/dd/yyyy"
                  selectedDate={selectedDateFromCalendarHook}
                />
                <Wrapper className={styles.dateFilterActions} margin={{ top: 'sm', bottom: 'sm' }}>
                  <Button secondary margin={{ left: 'sm' }} onClick={handleGoToToday}>
                    Go to Today
                  </Button>
                </Wrapper>
              </Wrapper>
              <Wrapper margin={{ vertical: 'md' }} className={styles.publishedPages}>
                {hasGroupsWithDate ? (
                  dateGroups.map((dateGroup, idx) => (
                    <Wrapper key={dateGroup.title}>
                      {dateGroup.dates && (
                        <>
                          <RevisionList
                            title={dateGroup.title}
                            type={dateGroup.type}
                            onSelectRevision={handleSelectRevision}
                            selectedDate={selectedDate}
                            dates={filterContentByDate(dateGroup, selectedDate)}
                          />
                          {idx < dateGroups.length - 1 && <Divider margin={{ all: 'md' }} />}
                        </>
                      )}
                    </Wrapper>
                  ))
                ) : (
                  <>
                    {areRevisionsLoading ? (
                      <ResponsiveLoader />
                    ) : (
                      <Wrapper margin={{ horizontal: 'md' }}>
                        <Typography size="sm" data-testid="page-no-history-alert">
                          This date has no published or scheduled revisions.
                        </Typography>
                      </Wrapper>
                    )}
                  </>
                )}
              </Wrapper>
            </div>,
            document.body
          )}
      </Dropdown.Content>
    </Dropdown>
  );
};
